import { describeLocationType } from "@/Data/describeLocationType";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { capitalize } from "@/Helpers/capitalize";
import { totalWeightForLineItems } from "@/Helpers/lineItemCalculations";
import moment from "moment";
import { BookShipmentDescribeConfirmQuote } from "../CustomComponents/BookShipmentDescribeConfirmQuote";
import type { BookShipmentShipmentType } from "@/Shared/Types/bookShipmentSliceTypes";
import { BookShipmentCalendarInfoIcon } from "../CustomComponents/BookShipmentCalendarInfoIcon";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";

export function useBookShipmentAdditionalContentProvider(
  pickupDate: string | undefined,
  _pickup: AddLocationLocationType | undefined,
  _delivery: AddLocationLocationType | undefined,
  lineItems: Array<AddLineItemType>,
  creditCardBrand: string | undefined,
  shipment: BookShipmentShipmentType,
) {
  function additionalContentProvider(flowIdentifier: string) {
    function describePickupDate() {
      if (pickupDate === undefined) {
        return <></>;
      }

      return (
        <div>Pickup: {moment(pickupDate).format("dddd, MMMM Do YYYY")}</div>
      );
    }

    function describeShipmentContents() {
      if (
        lineItems.length === 0 ||
        lineItems[0]?.weightPerHandlingUnit === undefined
      ) {
        return <></>;
      }
      return <div>Total Weight: {totalWeightForLineItems(lineItems)}lb</div>;
    }

    function describeAddress(location: AddLocationLocationType) {
      const detailParts = [
        location.address.city,
        location.address.stateOrProvinceCode,
        location.address.postalCode,
      ];

      const detailsDescription = detailParts.filter((p) => p).join(", ");

      const parts = [
        `Type: ${describeLocationType(location.locationType, location.distributionWarehouseBrand)}`,
        location.businessName,
        location.address.addressLine,
        detailsDescription,
      ];

      return parts
        .filter((p) => p)
        .map((part) => {
          return <div key={part}>{part}</div>;
        });
    }

    function describeBroker() {
      if (shipment === undefined || shipment.broker === undefined) {
        return <></>;
      }
      const detailParts = [
        shipment.broker.address.city,
        shipment.broker.address.stateOrProvinceCode,
        shipment.broker.address.postalCode,
      ];

      const detailsDescription = detailParts.filter((p) => p).join(", ");

      const parts = [
        shipment.broker.businessName,
        shipment.broker.address.addressLine,
        detailsDescription,
      ];

      return parts
        .filter((p) => p)
        .map((part) => {
          return <div key={part}>{part}</div>;
        });
    }

    function describePickupAddress() {
      if (!shipment) {
        return <></>;
      }
      return describeAddress(shipment.pickupLocation);
    }

    function describeDeliveryAddress() {
      if (!shipment) {
        return <></>;
      }
      return describeAddress(shipment.deliveryLocation);
    }

    function describePayment() {
      if (!shipment) {
        return <></>;
      }

      if (!creditCardBrand || !shipment.stripePaymentMethodId) {
        return <></>;
      }

      return <Row>{capitalize(creditCardBrand)} Securely Stored</Row>;
    }

    function describeDeliveryWindow() {
      if (!shipment || !shipment.selectedQuote) {
        return <></>;
      }

      const quote = shipment.selectedQuote;
      const expectedDeliveryDate = quote.expectedDeliveryDate;
      const latestExpectedDeliveryDate = quote.latestEstimatedDeliveryDate;

      if (!expectedDeliveryDate) {
        return <></>;
      }

      const earliestDate = moment(expectedDeliveryDate);
      const showLatestDate =
        latestExpectedDeliveryDate &&
        earliestDate.isBefore(moment(latestExpectedDeliveryDate));

      let dateText = "";
      if (showLatestDate) {
        const latestDate = moment(latestExpectedDeliveryDate);
        const earliestFormatted = earliestDate.format("Do MMMM");
        const latestFormatted = latestDate.format("Do MMMM");
        dateText = `${earliestFormatted} - ${latestFormatted}`;
      } else {
        dateText = earliestDate.format("Do MMMM");
      }

      return (
        <Row gap={2}>
          <Icon
            icon={BookShipmentCalendarInfoIcon}
            size="2xl"
            color="text-blue-text"
          />
          <Stack gap={0} align="start">
            {/* `brand` is the same #4c62ea as the icon's blue. */}
            <T size="xs" color="brand">
              Delivery Window
            </T>
            <T size="sm" weight="bold" color="brand">
              {dateText}
            </T>
          </Stack>
        </Row>
      );
    }

    if (flowIdentifier === "details") {
      return (
        <Stack gap={0} align="start">
          {describePickupDate()}
          {describeShipmentContents()}
        </Stack>
      );
    } else if (flowIdentifier === "quotes") {
      return (
        <Stack gap={2} align="start">
          <BookShipmentDescribeConfirmQuote shipment={shipment} />
          {describeDeliveryWindow()}
        </Stack>
      );
    } else if (flowIdentifier === "pickup-address") {
      return describePickupAddress();
    } else if (flowIdentifier === "delivery-address") {
      return describeDeliveryAddress();
    } else if (flowIdentifier === "broker") {
      return describeBroker();
    } else if (flowIdentifier === "dangerous-goods") {
      return "";
    } else if (flowIdentifier === "payment") {
      return describePayment();
    } else if (flowIdentifier === "review") {
      return "";
    } else {
      return "";
    }
  }
  return { additionalContentProvider };
}
